import { ARTICLE_SOCIAL_IMAGES } from './articleSocialImages.ts'
import type { AiCompanyArticle } from './aiArticleStore.js'
import { requiresCustomArticleImage } from './articleImageContract.ts'
import { withApprovedArticleImage } from './articleImageOverrides.ts'

const LOCAL_EDITORIAL_IMAGE = /^\/editorial\/[a-z0-9][a-z0-9/_-]*\.(?:jpe?g|png|webp)$/i
const STORED_EDITORIAL_IMAGE = /^(?:articleImages|omikinaArticleImages|runtimeCache)\/[a-z0-9][a-z0-9/_-]*$/i

const DETERMINISTIC_ARTICLE_IMAGES = {
  aiCompute: '/editorial/category-fallbacks/ai-compute.webp',
  capital: '/editorial/category-fallbacks/capital.webp',
  communities: '/editorial/category-fallbacks/communities.webp',
  dataCenters: '/editorial/category-fallbacks/data-centers.webp',
  government: '/editorial/category-fallbacks/government.webp',
  grid: '/editorial/category-fallbacks/grid.webp',
  nuclearFuel: '/editorial/category-fallbacks/nuclear-fuel.webp',
  power: '/editorial/category-fallbacks/power.webp',
  robotics: '/editorial/category-fallbacks/robotics-system.webp',
} as const

export type ArticleImageCoverage = 'original-static' | 'stored-original' | 'deterministic-fallback' | 'custom-required'
export type ArticleImageMetadata = {
  contentType: 'image/jpeg' | 'image/png' | 'image/webp'
  width?: number
  height?: number
}

const KNOWN_STATIC_DIMENSIONS: Record<string, readonly [number, number]> = {
  '/editorial/2026-09-02/clarapetra-pretty-machine-living-world-1200x630.webp': [1200, 630],
  '/editorial/2026-09-01/ai-news-source-reporters-synthesis-1200x630.webp': [1200, 630],
  '/editorial/2026-09-01/ai-token-tax-worker-meter-1200x630.webp': [1200, 630],
  '/editorial/2026-09-01/robot-dexterity-wrist-1200x630.webp': [1200, 630],
  '/editorial/2026-09-01/cyber-patch-window-financial-stability-1200x630.webp': [1200, 630],
  '/editorial/2026-08-31/multidisciplinary-agent-team-1200x630.webp': [1200, 630],
  '/editorial/2026-08-31/ai-permission-era-1200x630.webp': [1200, 630],
  '/editorial/2026-08-31/anthropic-music-data-lineage-1200x630.webp': [1200, 630],
  '/editorial/2026-08-31/china-ai-chip-earnings-test-1200x630.webp': [1200, 630],
  '/editorial/2026-08-22/omikina-platform-update.webp': [1599, 900],
  '/editorial/2026-08-27/cloud-thermal-resilience-watch.webp': [1200, 630],
  '/editorial/2026-08-27/omikina-intelligence-desk.webp': [1600, 836],
  '/editorial/2026-08-28/ai-cyber-defense-window.webp': [1536, 1024],
  '/editorial/2026-08-28/ai-improves-ai-alignment-loop-1200x630.webp': [1200, 630],
  '/editorial/2026-08-28/ai-improves-ai-alignment-loop.webp': [1536, 864],
  '/editorial/2026-08-28/omikina-robot-news-desk.webp': [1200, 630],
  '/editorial/2026-08-30/agent-sandbox-failure-1200x630.webp': [1200, 630],
  '/editorial/2026-08-30/omikina-global-cybersecurity-desk.webp': [1200, 630],
}

export function optionalMediaEarliestDate(value: string | undefined, settingName: string): string | undefined {
  const cleaned = value?.trim()
  if (!cleaned) return undefined
  if (!/^\d{4}-\d{2}-\d{2}$/.test(cleaned)) throw new Error(`invalid_${settingName}`)
  return cleaned
}

export function mediaDateIsEligible(date: string, earliestDate?: string): boolean {
  return /^\d{4}-\d{2}-\d{2}$/.test(date) && (!earliestDate || date >= earliestDate)
}

export function isTrustedArticleImagePath(value: string | undefined): boolean {
  if (!value || value.includes('..')) return false
  if (LOCAL_EDITORIAL_IMAGE.test(value)) return true
  if (!STORED_EDITORIAL_IMAGE.test(value)) return false
  // Firestore document paths contain an even number of non-empty segments.
  return value.split('/').filter(Boolean).length % 2 === 0
}

export function deterministicArticleImagePath(article: Pick<AiCompanyArticle, 'headline' | 'tags'>): string {
  const subject = `${article.tags.join(' ')} ${article.headline}`.toUpperCase()
  if (/ROBOT|AUTONOMOUS (?:MACHINE|VEHICLE)|EMBODIED/.test(subject)) return DETERMINISTIC_ARTICLE_IMAGES.robotics
  if (/DATA[ -]?CENTER|CLOUD|COMPUTE CAMPUS|COOLING/.test(subject)) return DETERMINISTIC_ARTICLE_IMAGES.dataCenters
  if (/NUCLEAR|URANIUM|FUEL CYCLE/.test(subject)) return DETERMINISTIC_ARTICLE_IMAGES.nuclearFuel
  if (/ELECTRIC GRID|TRANSMISSION|INTERCONNECT/.test(subject)) return DETERMINISTIC_ARTICLE_IMAGES.grid
  if (/POWER|ENERGY|ELECTRIC|THERMAL/.test(subject)) return DETERMINISTIC_ARTICLE_IMAGES.power
  if (/CAPITAL|FINANC|INVEST|FUNDING|MARKET/.test(subject)) return DETERMINISTIC_ARTICLE_IMAGES.capital
  if (/COMMUNIT|LABOR|WORKER|PUBLIC|SOCIAL/.test(subject)) return DETERMINISTIC_ARTICLE_IMAGES.communities
  if (/GOVERN|POLICY|REGULAT|SECURITY|CYBER|DEFEN[CS]E/.test(subject)) return DETERMINISTIC_ARTICLE_IMAGES.government
  return DETERMINISTIC_ARTICLE_IMAGES.aiCompute
}

export function articleImageCoverage(article: AiCompanyArticle): ArticleImageCoverage {
  if (requiresCustomArticleImage(article) && !hasCustomArticleImage(article)) return 'custom-required'
  if (!isTrustedArticleImagePath(article.imagePath)) return 'deterministic-fallback'
  if (article.imageModel === 'OMIKINA deterministic editorial fallback v1'
    || Object.values(DETERMINISTIC_ARTICLE_IMAGES).includes(article.imagePath as typeof DETERMINISTIC_ARTICLE_IMAGES[keyof typeof DETERMINISTIC_ARTICLE_IMAGES])) {
    return 'deterministic-fallback'
  }
  return article.imagePath!.startsWith('/') ? 'original-static' : 'stored-original'
}

export function hasCustomArticleImage(article: AiCompanyArticle): boolean {
  if (!isTrustedArticleImagePath(article.imagePath) || article.imagePath?.startsWith('/editorial/category-fallbacks/')
    || article.imageModel === 'OMIKINA deterministic editorial fallback v1') return false
  if (article.imagePath?.startsWith('omikinaArticleImages/')) {
    // The server's mergeArticleImageRecord verifies the current story hash before
    // exposing these fields. Browser consumers use that validated metadata only.
    return article.imageStatus === 'ready' && /^[a-f0-9]{64}$/.test(article.imageInputHash ?? '')
      && /^[a-f0-9]{64}$/.test(article.imageGenerationKey ?? '')
  }
  if (requiresCustomArticleImage(article)) {
    // Future manually supplied images require an inspected, exact-story approval,
    // not merely a reusable path or a self-declared provider/model name.
    const approved = withApprovedArticleImage(article)
    return approved !== article && approved.imagePath === article.imagePath
  }
  return true
}

/** Shared, browser-safe disclosure for article figures and cards. */
export function articleImageCaption(article: AiCompanyArticle): string {
  const coverage = articleImageCoverage(article)
  if (coverage === 'custom-required' || (!article.imagePath && ['pending', 'blocked', 'requesting', 'uncertain', 'failed'].includes(article.imageStatus ?? ''))) {
    const reasons: Record<string, string> = {
      story_specific_image_required: 'story-specific artwork is pending',
      article_image_generation_disabled: 'image generation has not been enabled',
      article_image_daily_cap_reached: 'the daily image allowance has been reached',
      article_image_approval_reference_missing: 'image generation approval is pending',
      article_image_cost_guard_unconfigured: 'the image allowance has not been configured',
    }
    const reason = reasons[article.imageFailureReason ?? ''] ?? (article.imageStatus === 'failed' || article.imageStatus === 'uncertain'
      ? 'the image request needs review' : 'story-specific artwork is pending')
    return `Story-specific image unavailable: ${reason}.`
  }
  if (coverage === 'deterministic-fallback') return 'Category illustration; not a story-specific image.'
  if (/openai|imagegen|gpt-image|ai-generated/i.test(article.imageModel ?? '') || article.imageStatus === 'ready') {
    return 'AI-generated story-specific editorial illustration; not documentary evidence.'
  }
  return 'Editorial illustration.'
}

export function withArticleImageCoverage(article: AiCompanyArticle): AiCompanyArticle {
  article = withApprovedArticleImage(article)
  const coverage = articleImageCoverage(article)
  if (coverage === 'custom-required') {
    return { ...article, customImageRequired: true, imagePath: undefined, imageModel: undefined,
      imageContentType: undefined, imageWidth: undefined, imageHeight: undefined,
      imageStatus: article.imageStatus === 'ready' ? 'pending' : article.imageStatus ?? 'pending',
      imageFailureReason: article.imageFailureReason ?? 'story_specific_image_required', homepagePromotionEligible: false }
  }
  const fallback = coverage === 'deterministic-fallback'
  // Preserve a selected category illustration and its matching alt text.
  // Derive a new fallback only when an article has no usable selected artwork.
  const imagePath = isTrustedArticleImagePath(article.imagePath) ? article.imagePath! : deterministicArticleImagePath(article)
  const imageAlt = article.imageAlt?.trim()
    || `${fallback ? 'Deterministic' : 'Original'} OMIKINA editorial illustration for ${article.headline}.`
  const metadata = articleImageMetadata({ ...article, imagePath })
  return {
    ...article,
    customImageRequired: requiresCustomArticleImage(article),
    imageStatus: article.imageStatus ?? (fallback ? 'fallback' : 'custom'),
    imagePath,
    imageAlt,
    imageContentType: metadata?.contentType,
    imageWidth: metadata?.width,
    imageHeight: metadata?.height,
    imageModel: fallback ? 'OMIKINA deterministic editorial fallback v1' : article.imageModel,
  }
}

export function articleImageUrl(article: AiCompanyArticle, siteUrl = 'https://www.omikina.com'): string | undefined {
  if (!isTrustedArticleImagePath(article.imagePath)) return undefined
  const base = siteUrl.replace(/\/$/, '')
  return article.imagePath!.startsWith('/')
    ? `${base}${article.imagePath}`
    : `${base}/api/ai-article-image?id=${encodeURIComponent(article.id)}${article.imageGenerationKey ? `&version=${article.imageGenerationKey}` : ''}`
}

export function articleImageMetadata(article: AiCompanyArticle): ArticleImageMetadata | undefined {
  if (!isTrustedArticleImagePath(article.imagePath)) return undefined
  const path = article.imagePath!
  const verified = ARTICLE_SOCIAL_IMAGES[path]
  if (verified) return { contentType: verified.sourceType, width: verified.sourceWidth, height: verified.sourceHeight }
  const validDimension = (value?: number) => typeof value === 'number' && Number.isInteger(value) && value > 0
  if (article.imageContentType && validDimension(article.imageWidth) && validDimension(article.imageHeight)) {
    return { contentType: article.imageContentType, width: article.imageWidth, height: article.imageHeight }
  }
  if (!path.startsWith('/') && !article.imageContentType) return undefined
  const contentType = /\.png$/i.test(path) ? 'image/png' as const
    : /\.jpe?g$/i.test(path) ? 'image/jpeg' as const
      : 'image/webp' as const
  let dimensions = KNOWN_STATIC_DIMENSIONS[path]
  if (!dimensions && /^\/editorial\/2026-08-22\/(?!omikina-platform-update\.webp$)[a-z0-9-]+\.webp$/i.test(path)) dimensions = [1439, 810]
  if (!dimensions && path.startsWith('/editorial/category-fallbacks/')) dimensions = [1536, 864]
  return dimensions
    ? { contentType, width: dimensions[0], height: dimensions[1] }
    : { contentType }
}

/** A podcast cover is channel artwork, never substituted into article artwork fields. */
export function articlePodcastArtwork(article: AiCompanyArticle, siteUrl = 'https://www.omikina.com') {
  const imageUrl = articleImageUrl(article, siteUrl); const metadata = articleImageMetadata(article)
  if (imageUrl && metadata) return { imageUrl, imageAlt: article.imageAlt, ...metadata }
  return { imageUrl: `${siteUrl.replace(/\/$/, '')}/brand/omikina/final/profile/omikina-profile-2048.png`,
    imageAlt: 'OMIKINA podcast channel cover; story-specific article artwork is pending.',
    contentType: 'image/png' as const, width: 2048, height: 2048 }
}

export function isArticleMediaEligible(article: AiCompanyArticle): boolean {
  if (!/^[a-f0-9]{20}$/.test(article.id)) return false
  if (!Number.isFinite(new Date(article.generatedAt).getTime())) return false
  if (!article.headline.trim()) return false
  // Image work has its own queue; pending art must not suppress authorized text/audio output.
  if (!requiresCustomArticleImage(article) && (!article.imageAlt.trim() || !isTrustedArticleImagePath(article.imagePath))) return false
  const narrative = [article.dek, ...article.sections.flatMap((section) => section.paragraphs), article.whyItMatters.text]
  return narrative.some((value) => value.trim().length > 0)
}

export const ARTICLE_DETERMINISTIC_IMAGE_PATHS = Object.freeze(Object.values(DETERMINISTIC_ARTICLE_IMAGES))
